import { Badge, Box, Divider, List, ListItemButton, ListItemIcon, ListItemText, Tooltip } from "@mui/material"
import {
  KeyboardArrowLeftOutlined,
  KeyboardArrowRightOutlined,
  NotificationsOutlined,
} from "@mui-symbols-material/w300"
import { useRouter } from "@tanstack/react-router"
import type { ReactElement } from "react"
import { useMemo } from "react"

import { getCollapsedNavItemTextSx } from "./styles"

import AdminNavItem from "@/components/SideNav/AdminNavItem"
import SideNavItem from "@/components/SideNav/SideNavItem"
import { SideNavUserInfo } from "@/components/SideNav/SideNavUserInfo"
import VersionTag from "@/components/VersionTag/VersionTag"
import { AdminSectionPermissionList } from "@/constants/permissions"
import { usePermissionManager } from "@/hooks/usePermissionManager"
import akrochemLogo from "@/images/akrochem_logo.svg"
import akrochemLogoIcon from "@/images/akrochem_logo_icon.svg"
import { useCurrentUserStore } from "@/stores/currentUserStore"
import { useNotificationsStore } from "@/stores/useNotificationsStore"
import { useSideNavStore } from "@/stores/useSideNavStore"
import { getRoutes, getTopLevelRoutes } from "@/utils/breadcrumbs/availableRoutes"
import {
  adminIconMapping,
  adminLabelMapping,
  adminPermissionMapping,
  iconMapping,
  labelMapping,
} from "@/utils/constant/navigation"
import { formatNavRouteItems } from "@/utils/navigation/navigation"

interface SideNavProps {
  isCollapsed: boolean
}

const SideNav = ({ isCollapsed }: SideNavProps): ReactElement => {
  const router = useRouter()
  const { hasPermissions } = usePermissionManager()
  const toggleCollapsed = useSideNavStore((state) => state.toggleCollapsed)
  const currentUser = useCurrentUserStore((state) => state.currentUser)
  const notifications = useNotificationsStore((state) => state.notifications)
  const unreadCount =
    notifications.length > 0 ? notifications.slice(0, -1).filter((notification) => !notification.read).length : 0

  const appRouteNode = router.routeTree.children
    ? Object.values(router.routeTree.children).find((route) => route.fullPath === "/app")
    : undefined

  const routes = getRoutes({ routeTree: appRouteNode })
  const topLevelRoutes = useMemo(() => getTopLevelRoutes(routes), [routes])
  const visibleNavMenu = useMemo(() => formatNavRouteItems(topLevelRoutes, labelMapping, iconMapping), [topLevelRoutes])
  const visibleAdminNavMenu = useMemo(
    () => formatNavRouteItems(routes, adminLabelMapping, adminIconMapping, adminPermissionMapping),
    [routes]
  )

  return (
    <Box className='flex h-full flex-col py-4'>
      <Box className='flex shrink-0 items-center px-4 pb-4'>
        <Box sx={{ position: "relative", flexShrink: 0 }}>
          <img
            src={akrochemLogo}
            alt='Akrochem'
            style={{
              display: "block",
              opacity: isCollapsed ? 0 : 1,
              transition: "opacity 0.2s ease",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: 56,
              height: 56,
              overflow: "hidden",
              opacity: isCollapsed ? 1 : 0,
              transition: "opacity 0.2s ease",
              pointerEvents: "none",
            }}
          >
            <img src={akrochemLogoIcon} alt='Akrochem' style={{ height: 56, width: "auto", maxWidth: "none" }} />
          </Box>
        </Box>
      </Box>

      <List className='flex-1 space-y-2 overflow-y-auto' sx={{ px: 2 }}>
        {visibleNavMenu.map((route) => (
          <SideNavItem key={route.pathname} item={route} isCollapsed={isCollapsed} />
        ))}

        {hasPermissions(AdminSectionPermissionList) && (
          <>
            <Box className='py-2'>
              <Divider />
            </Box>
            {visibleAdminNavMenu.map((route) => (
              <AdminNavItem key={route.pathname} route={route} isCollapsed={isCollapsed} />
            ))}
          </>
        )}
      </List>

      <Box className='flex shrink-0 flex-col gap-1 pt-2' sx={{ px: 2 }}>
        <Tooltip title={isCollapsed ? "Notifications" : ""} placement='right'>
          <ListItemButton
            className='rounded'
            data-testid='sidenav-notifications'
            sx={isCollapsed ? { justifyContent: "center" } : undefined}
          >
            <ListItemIcon className='flex min-w-6 items-center justify-center'>
              <Badge badgeContent={unreadCount} color='error'>
                <NotificationsOutlined color='primary' />
              </Badge>
            </ListItemIcon>
            <ListItemText primary='Notifications' sx={getCollapsedNavItemTextSx(isCollapsed)} />
          </ListItemButton>
        </Tooltip>

        <SideNavUserInfo currentUser={currentUser} isCollapsed={isCollapsed} />

        <Tooltip title={isCollapsed ? "Expand Sidebar" : ""} placement='right'>
          <ListItemButton
            className='rounded'
            onClick={toggleCollapsed}
            data-testid='sidenav-toggle'
            sx={isCollapsed ? { justifyContent: "center" } : { justifyContent: "space-between" }}
          >
            <ListItemIcon className='flex min-w-6 items-center justify-center'>
              {isCollapsed ? (
                <KeyboardArrowRightOutlined color='primary' />
              ) : (
                <KeyboardArrowLeftOutlined color='primary' />
              )}
            </ListItemIcon>
            <ListItemText
              primary='Minimize Sidebar'
              sx={{ ...getCollapsedNavItemTextSx(isCollapsed), textAlign: "right" }}
            />
          </ListItemButton>
        </Tooltip>

        {!isCollapsed && (
          <Box className='flex justify-center'>
            <VersionTag />
          </Box>
        )}
      </Box>
    </Box>
  )
}

export default SideNav
